import React, { useState, useEffect } from "react";
import { format } from 'date-fns';
import styles from "./ReviewPopup.module.css";
import cn from 'classnames';
import { Image, Modal } from "react-bootstrap";
import CustomButton, { ButtonSize, ButtonType } from "../../common/CustomButton/CustomButton";
import { ASSESTS } from "../../../constant/assest";
import { IManagementStatus } from "../../../constant/enums";
import FormCheckbox from "../../common/FormField/FormCheckbox";
import FormTextArea from "../../common/FormField/FormTextArea";
import { IContentItem } from "../../../redux/slices/listIcon/types";
import { Card } from "react-bootstrap";
import { getColorForUser } from "../../common/UserCard/UserCard";
import LocalStorageService, { LocalStorageKeys } from '../../../services/localStorageService';
import { ReviewPopupSkeleton } from "./ReviewPopupSkeleton";
import { categoryOptions } from '../../../pages/unAuth/ListIcons/component/UploadNewContent/UploadNewContent';
import { UserType } from "../../../constant/enums";
import { User } from "../../../redux/slices/auth/types";

interface IReviewPopupProps {
    content: IContentItem | null;
    loading: boolean;
    showModal: boolean;
    onHide: () => void;
    onUpdate: (data: any) => void;
    onDelete: (content: IContentItem) => void;
    className?: string;
};

export const USER_TYPE_LABELS: Record<string, string> = {
  superAdmin: "Super Admin",
  subAdmin: "Sub Admin",
  user: "User",
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  admin: "Admin",
  sponsored: "Sponsored",
};

const getCategoryLabel = (value: string | null) => {
    const option = categoryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
};

const ReviewPopup: React.FC<IReviewPopupProps> = ({content, loading, showModal, onHide, onUpdate, onDelete, className}) => {
    const [adminNotes, setAdminNotes] = useState('');
    const [isAppropriate, setIsAppropriate] = useState(false);
    const [isHighQuality, setIsHighQuality] = useState(false);
    const [isAccurate, setIsAccurate] = useState(false);
    const [isSpam, setIsSpam] = useState(false);
    const user = LocalStorageService.get<User>(LocalStorageKeys.USER);
    const isSuperAdmin = user?.role === UserType.SUPER_ADMIN;

    useEffect(() => {
        if (content) {
            setAdminNotes(content.admin_notes || '');
            setIsAppropriate(content.quality?.is_approprite_content || false);
            setIsHighQuality(content.quality?.image_hign_quality || false);
            setIsAccurate(content.quality?.is_info_accurate || false);
            setIsSpam(content.quality?.is_spam || false);
        }
    }, [content]);

    const handleUpdate = (status: 'published' | 'rejected') => {
        if (content) {
            onUpdate({
                id: content.id,
                status,
                admin_notes: adminNotes,
                is_appropriate_content: isAppropriate,
                image_high_quality: isHighQuality,
                is_info_accurate: isAccurate,
                is_spam: isSpam,
            });
        }
    };

    const renderMetaItem = (icon: string, label: string, value: string | null) => {
        return (
            <div className={styles.metaItem}>
                <div className={styles.metaIcon}>
                    <Image src={icon} alt={label} />
                </div>
                <div className={styles.metaDetails}>
                    <div className={styles.metaLabel}>{label}</div>
                    <div className={styles.metaValue}>{value}</div>
                </div>
            </div>
        );
    };
    const bannerFile= content?.uploads[0] ? content?.uploads[0]?.fileUrl : '';
    const userImage= content?.user?.profile_image;
    const userName= content?.user?.full_name;
    const bgColor = getColorForUser(userName || 'N/A');

    const isVideo = (url: any) => {
        const videoExtensions = ['mp4', 'mov', 'webm', 'mkv', 'avi'];
        const ext = url.split('?')[0].split('.').pop().toLowerCase();
        return videoExtensions.includes(ext);
    };

  return (
    <div className={cn(styles.customGoogleMap, className)}>
      <Modal
          show={showModal}
          onHide={onHide}
          size="lg"
          centered
          dialogClassName={styles.modalDialogue}
        >
          <Modal.Header bsPrefix={cn(styles.modalHeader)}>
            <div className={styles.headerTitle}>
                Content Review
                {content && <span className={cn(styles.status, styles[content.status.toLowerCase() || ''])}>
                    <Image src={content.status === IManagementStatus.REJECTED ? ASSESTS.REJECT : ASSESTS.REVIEW} alt='In Review' width={12} height={12} /> {content.status === IManagementStatus.INREVIEW ? 'In Review' : 
                     content.status === IManagementStatus.PUBLISHED ? 'Published' : content.status === IManagementStatus.DRAFT ? 'Draft' : 'Rejected'
                    }
                </span>}               
            </div>
            <CustomButton 
              btnType={ButtonType.NOSTYLE}
              onClick={onHide}
              className={styles.closeModalBtn}
            >
              Close
            </CustomButton>
          </Modal.Header>
          <Modal.Body bsPrefix={cn(styles.modalBody)}>
            {loading ? (
                <ReviewPopupSkeleton /> 
            ) : content && (
                <>
                    <div className={styles.imageWrap}>
                        <span className={styles.tag}>{CONTENT_TYPE_LABELS[content.content_type]}</span>
                        <div className={styles.fileContainer}>
                        { isVideo(bannerFile) ? (
                            <video src={bannerFile} controls muted loop className={styles.contentListCardImg} />
                        ) : (
                            <Card.Img
                                src={bannerFile ? bannerFile : ASSESTS.CONTENTIMAGEPLACEHOLDER}
                                onError={(e) => (e.currentTarget.src = ASSESTS.CONTENTIMAGEPLACEHOLDER)}
                                alt={''}
                                bsPrefix={styles.contentListCardImg}
                            />
                            )
                        }
                        </div>                       
                    </div>
                    <div className={styles.titleWrap}>
                        <div className={styles.title}>{content.title}</div>
                        <div className={styles.subTitle}>{content.desc}</div>
                    </div>
                    <div className={styles.metaWrap}>
                        <div className={styles.metaItem}>
                            <div className={styles.metaIcon}>
                                <Image src={ASSESTS.AUTHOR} alt='Author' />
                            </div>
                            <div className={styles.metaDetails}>
                                <div className={styles.metaLabel}>Author</div>
                                <div className={styles.metaAuthor}>
                                    <div className={cn(styles.metaAuthorImage, !userImage && styles.userAvatarText)}
                                        style ={!userImage ? { backgroundColor: bgColor } : undefined}>
                                        {userImage ? 
                                        <Image src={userImage} alt={userName} width={33} height={33}/> : 
                                        (userName ? userName.trim()?.charAt(0).toUpperCase() : 'N')
                                    }                   
                                    </div>
                                    <div className={styles.metaAuthorName}>{content.user.full_name}</div>
                                    <div className={styles.metaAuthorTag}>{USER_TYPE_LABELS[content.user.user_type] || "Unknown"}</div>
                                </div>
                            </div>
                        </div>
                        {renderMetaItem(ASSESTS.CONTENTTYPE, 'Submitted', content.created_at ? format(new Date(content.created_at), 'MMMM d, yyyy') : 'N/A')}
                        {renderMetaItem(ASSESTS.SUBMITED, 'Content type', CONTENT_TYPE_LABELS[content.content_type] || content.content_type)}
                        {renderMetaItem(ASSESTS.CATEGORY, 'Category', getCategoryLabel(content.category_list_type))}
                    </div>
                    <div className={styles.fullContent}>
                        <div className={styles.label}>Full Content</div>
                        <div className={styles.contentIn}>
                            <div className={styles.contentText}>
                                {content.desc}
                            </div>
                        </div>
                    </div>
                  
                        <div className={styles.fullContent}>
                            <FormTextArea 
                                label={'Admin Notes'}
                                className={styles.admiNotes}
                                disabled= {isSuperAdmin ? false : true}
                                placeholder="Add notes or feedback about this content..."
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                            />
                        </div>
                        <div className={styles.fullContent}>
                            <div className={styles.label}>Reasons for Rejection (if any)</div>
                            {/* <div className={styles.label}>Quality Checklist</div> */}
                            <div className={styles.qualityCheck}>
                                <div className={styles.qualityCheckItem}>
                                    <FormCheckbox label={"Content is inappropriate and doesn't follows community guidelines"} checked={isAppropriate} disabled={!isSuperAdmin} onChange={(e) => setIsAppropriate(e.target.checked)} />
                                </div>
                                <div className={styles.qualityCheckItem}>
                                    <FormCheckbox label={"The uploaded file is not high-quality and is irrelevant"} checked={isHighQuality} disabled={!isSuperAdmin} onChange={(e) => setIsHighQuality(e.target.checked)} />
                                </div>
                                <div className={styles.qualityCheckItem}>
                                    <FormCheckbox label={"Information is inaccurate and not helpful"} checked={isAccurate} disabled={!isSuperAdmin} onChange={(e) => setIsAccurate(e.target.checked)} />
                                </div>
                                <div className={styles.qualityCheckItem}>
                                    <FormCheckbox label={"Spam or irrelevant content"} checked={isSpam} disabled={!isSuperAdmin} onChange={(e) => setIsSpam(e.target.checked)} />
                                </div>
                            </div>
                        </div>
                    
                </>
            )}
          </Modal.Body>
          <Modal.Footer bsPrefix={styles.modalFooter}>
            {/* {isSuperAdmin && content?.status !== IManagementStatus.PUBLISHED && content?.status !== IManagementStatus.REJECTED &&                 
                <div className={styles.footerText}>Review this content carefully before approving or rejecting</div>
            } */}
            <div className={styles.footerText}>Review this content carefully</div>
            <div className={styles.footerBtns}>
                <CustomButton
                    onClick={onHide}
                    btnType={ButtonType.LIGHT}
                    btnSize={ButtonSize.SMALL}
                    className={cn(styles.footerBtn, styles.closeBtn)}
                >
                    Close
                </CustomButton>
                {isSuperAdmin && content?.status !== IManagementStatus.PUBLISHED && content?.status !== IManagementStatus.REJECTED && (
                    <>
                        <CustomButton
                            onClick={() => handleUpdate('rejected')}
                            btnType={ButtonType.RED}
                            btnSize={ButtonSize.SMALL}
                            className={cn(styles.footerBtn)}
                        >
                            Reject Content
                        </CustomButton>
                        <CustomButton
                            onClick={() => handleUpdate('published')}
                            btnType={ButtonType.GREEN}
                            btnSize={ButtonSize.SMALL}
                            className={cn(styles.footerBtn)}
                        >
                            Approve & Publish
                        </CustomButton>
                    </>
                )}
                { content?.status === IManagementStatus.DRAFT && user?.role === UserType.SUB_ADMIN && user.id === content.user_id && (
                    <CustomButton
                        onClick={() => handleUpdate('published')}
                        btnType={ButtonType.GREEN}
                        btnSize={ButtonSize.SMALL}
                        className={cn(styles.footerBtn)}
                    >
                        Approve & Publish
                    </CustomButton>
                )}
                {/* {isSuperAdmin && 
                    <CustomButton
                        onClick={() => onDelete(content!)}
                        btnType={ButtonType.RED}
                        btnSize={ButtonSize.SMALL}
                        className={cn(styles.footerBtn)}
                    >
                        Delete
                    </CustomButton>
                }               */}
            </div>
          </Modal.Footer>
        </Modal>
    </div>
  );
};

export default ReviewPopup;
